/**
 * Algebraic expression model for 6th-grade standards.
 * Represents expressions like 3x + 5 or 2(x + 4).
 */
import { Rational } from "./rational";

/** A single term: coefficient * variable^power, or a constant (variable=null). */
export interface Term {
  readonly coefficient: Rational;
  readonly variable: string | null;
  readonly power: number;
}

/** Build a constant term. */
export function constant(value: Rational): Term {
  return { coefficient: value, variable: null, power: 0 };
}

/** Build a variable term like 3x. */
export function varTerm(coefficient: Rational, variable = "x", power = 1): Term {
  return { coefficient, variable, power };
}

/** Evaluate an expression by substituting variable values. */
export function evaluate(terms: readonly Term[], values: Record<string, Rational>): Rational {
  let result = Rational.ZERO;
  for (const term of terms) {
    if (term.variable === null) {
      result = result.add(term.coefficient);
    } else {
      const val = values[term.variable];
      if (val === undefined) throw new Error(`No value for variable ${term.variable}`);
      let powered = Rational.ONE;
      for (let i = 0; i < term.power; i++) powered = powered.mul(val);
      result = result.add(term.coefficient.mul(powered));
    }
  }
  return result;
}

/** Format terms as LaTeX. */
export function termsToLatex(terms: readonly Term[]): string {
  if (terms.length === 0) return "0";
  const parts: string[] = [];
  for (const t of terms) {
    if (t.coefficient.isZero()) continue;

    let piece = "";
    const isFirst = parts.length === 0;

    if (t.variable === null) {
      if (!isFirst && t.coefficient.isPositive()) piece += "+ ";
      else if (!isFirst && t.coefficient.isNegative()) piece += "- ";
      piece +=
        t.coefficient.isNegative() && isFirst
          ? t.coefficient.toLatex()
          : t.coefficient.abs().toLatex();
    } else {
      const absCoeff = t.coefficient.abs();
      if (!isFirst && t.coefficient.isPositive()) piece += "+ ";
      else if (!isFirst && t.coefficient.isNegative()) piece += "- ";
      else if (isFirst && t.coefficient.isNegative()) piece += "-";

      if (!absCoeff.equals(Rational.ONE)) {
        piece += absCoeff.toLatex();
      }
      piece += t.variable;
      if (t.power > 1) piece += `^{${t.power}}`;
    }
    parts.push(piece);
  }
  return parts.join(" ") || "0";
}

/** Format terms as plain text. */
export function termsToText(terms: readonly Term[]): string {
  if (terms.length === 0) return "0";
  const parts: string[] = [];
  for (const t of terms) {
    if (t.coefficient.isZero()) continue;
    const isFirst = parts.length === 0;

    let piece = "";
    if (t.variable === null) {
      if (!isFirst && t.coefficient.isPositive()) piece += " + ";
      else if (!isFirst && t.coefficient.isNegative()) piece += " - ";
      piece +=
        t.coefficient.isNegative() && isFirst
          ? t.coefficient.toString()
          : t.coefficient.abs().toString();
    } else {
      const absCoeff = t.coefficient.abs();
      if (!isFirst && t.coefficient.isPositive()) piece += " + ";
      else if (!isFirst && t.coefficient.isNegative()) piece += " - ";
      else if (isFirst && t.coefficient.isNegative()) piece += "-";

      if (!absCoeff.equals(Rational.ONE)) piece += absCoeff.toString();
      piece += t.variable;
      if (t.power > 1) piece += `^${t.power}`;
    }
    parts.push(piece);
  }
  return parts.join("") || "0";
}

/** Compute base^exponent for non-negative whole-number exponent. */
export function power(base: Rational, exponent: number): Rational {
  if (exponent < 0) throw new RangeError("Negative exponents not supported");
  if (exponent === 0) return Rational.ONE;
  let result = Rational.ONE;
  for (let i = 0; i < exponent; i++) result = result.mul(base);
  return result;
}

/** Count the number of terms in an expression (ignoring zero-coefficient terms). */
export function termCount(terms: readonly Term[]): number {
  return terms.filter((t) => !t.coefficient.isZero()).length;
}

/**
 * Identify the parts of an expression for the "parts of an expression" topic.
 */
export interface ExpressionParts {
  readonly terms: number;
  readonly coefficients: readonly Rational[];
  readonly constants: readonly Rational[];
  readonly variables: readonly string[];
}

export function identifyParts(terms: readonly Term[]): ExpressionParts {
  const active = terms.filter((t) => !t.coefficient.isZero());
  return {
    terms: active.length,
    coefficients: active
      .filter((t) => t.variable !== null)
      .map((t) => t.coefficient),
    constants: active
      .filter((t) => t.variable === null)
      .map((t) => t.coefficient),
    variables: [
      ...new Set(
        active.filter((t) => t.variable !== null).map((t) => t.variable as string),
      ),
    ],
  };
}
